import { DatePicker, Flex, Form, InputProps } from 'antd';
import { Rule } from 'antd/es/form';
import { FormInstance } from 'antd/es/form/Form';
import { EditIcon, EyeIcon } from 'assets/svg';
import { ButtonAction, InputForm } from 'components';
import TableComponent from 'components/Table/TableComponent';
import dayjs from 'dayjs';
import useQueryParam from 'hooks/useQueryPrams';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { forwardRef, memo, ReactNode, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import path from 'routes/Path';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import {
  formatCurrency,
  formatDate,
  IExportExcels,
} from 'utils/constants/functions';
import { HocChangePagination } from 'utils/HocChangePagination';
import { IData } from './CNFollowPlan';

interface IRenderTable {
  rules?: Rule[];
  message: string;
  inputProps: InputProps;
  component?: ReactNode;
}

interface IProps {
  title?: any;
  data: IData;
  loading?: boolean;
  form?: FormInstance<any>;
  pagination?: boolean;
  differences: any[];
  refetch?: (type?: any, headerRow?: any) => void;
}

export const TableCNFollowPlan = memo(
  forwardRef(
    (
      { data, form, title, loading, pagination = true, refetch }: IProps,
      ref: any,
    ) => {
      const navigate = useNavigate();

      const queryParam = useQueryParam(),
        page = parseInt(queryParam.get('page') + '') || 1,
        page_size =
          parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

      // const isValid = (value: any) => {
      //   if (/^\d+$/.test(value)) {
      //     return Promise.resolve();
      //   } else return Promise.reject('Some message here');
      // };
      // const validatorRevenue = (value: string) => {
      //   if (Number(value) >= 0) return Promise.resolve();
      //   return Promise.reject('Không được nhập số âm');
      // };

      // const unit = 1000000;

      // const getCellStyle = (rowIndex: number, key: string) => {
      //   const isDifferent = differences.some(
      //     (difference) =>
      //       difference.rowIndex === rowIndex && difference.key === key,
      //   );
      //   return isDifferent ? { color: 'red' } : {};
      // };

      // const formatInputValue = (value: any) => {
      //   return value ? value : '';
      // };

      // const parseInputValue = (value: any) => {
      //   return parseFloat(value.replace(/\./g, '')?.replace(',', '.'));
      // };

      const renderTable = ({ rules, component, inputProps }: IRenderTable) => {
        const { name, value } = inputProps,
          customRules = rules ?? ([] as Rule[]);

        return (
          <Form.Item
            name={name}
            className="m-0"
            rules={customRules}
            initialValue={value}
          >
            {component || (
              <InputForm
                value={value}
                className="rounded-xl h-10"
                {...inputProps}
              />
            )}
          </Form.Item>
        );
      };

      const columns: ColumnsTypeCustom = [
        {
          width: 50,
          title: 'STT',
          type: 'number',
          className: 'align-top',
          render: (_, __, index) => index + 1 + page_size * (page - 1),
        },
        {
          width: 100,
          title: 'Năm',
          type: 'number',
          align: 'center',
          dataIndex: 'year',
          className: 'align-top',
          render(value, _, index) {
            const currentYear =
              typeof value === 'object' ||
              typeof value === 'string' ||
              typeof value === 'number'
                ? dayjs(value, 'YYYY').year().toString()
                : value.toString();

            const disabledDate = (currentDate: dayjs.Dayjs) => {
              return (
                dayjs(currentDate, 'YYYY').year() < new Date().getFullYear()
              );
            };

            return index === -1
              ? renderTable({
                  message: 'Nhập năm',
                  inputProps: {
                    name: 'year',
                  },
                  component: (
                    <DatePicker
                      picker="year"
                      inputReadOnly
                      value={currentYear}
                      disabledDate={disabledDate}
                    />
                  ),
                })
              : // : Number(currentYear);
                value;
          },
        },
        {
          width: 170,
          align: 'left',
          title: 'Tên chi nhánh',
          dataIndex: 'plant_name',
          className: 'align-top',
        },
        {
          width: 150,
          align: 'left',
          title: 'Tên đội',
          dataIndex: 'team_sales_name',
          className: 'align-top',
        },
        {
          width: 200,
          align: 'left',
          title: 'Loại doanh thu',
          dataIndex: 'type_revenue',
          className: 'align-top',
        },
        {
          type: 'number',
          currency: 'vi-VN',
          width: 200,
          align: 'right',
          title: 'Kế hoạch lũy kế',
          dataIndex: 'cumulative_plan',
          className: 'align-top',
          render: (value: number) => (value ? formatCurrency({ value }) : '-'),
        },
        {
          type: 'number',
          currency: 'vi-VN',
          width: 200,
          align: 'right',
          title: 'Kế hoạch năm',
          dataIndex: 'year_plan',
          className: 'align-top',
          render: (value: number) => (value ? formatCurrency({ value }) : '-'),
        },
        {
          type: 'number',
          currency: 'vi-VN',
          width: 200,
          align: 'right',
          title: 'Thực hiện',
          dataIndex: 'realized_revenue',
          className: 'align-top',
          render: (value: number) => (value ? formatCurrency({ value }) : '-'),
        },
        {
          width: 200,
          align: 'right',
          title: '% đạt được lũy kế',
          dataIndex: 'cumulative_plan_percent',
          className: 'align-top',
        },
        {
          width: 200,
          align: 'right',
          title: '% đạt được theo năm',
          dataIndex: 'percentage_achieved_year',
          className: 'align-top',
        },
        {
          width: 100,
          title: 'Giá trị',
          isShowRender: true,
          align: 'left',
          dataIndex: 'unit_money',
          className: 'align-top',
        },
        {
          width: 150,
          title: 'Người cập nhật',
          dataIndex: 'modify_name',
          align: 'left',
          className: 'align-top',
        },
        {
          width: 210,
          title: 'Ngày cập nhật',
          dataIndex: 'modify_date',
          align: 'center',
          className: 'align-top',
          type: 'date',
          render: (value) =>
            dayjs(value).isValid()
              ? formatDate(value, 'DD/MM/YYYY HH:mm:ss')
              : value,
        },
        {
          fixed: 'right',
          align: 'center',
          width: 50,
          render: (_, record) => (
            <Flex justify="center" gap={10}>
              <ButtonAction
                tooltip={'Xem chi tiết'}
                onClick={() =>
                  navigate(`${path.CN_FOLLOW_PLAN_DETAIL}`, { state: record })
                }
              >
                <EyeIcon />
              </ButtonAction>
            </Flex>
          ),
        },
      ];

      useImperativeHandle(ref, () => ({
        getData,
        handleExportExcel,
        headerRow: columns,
      }));

      const getData = (): IExportExcels => ({
        headerRow: columns,
        datas: data.items,
        isShowSTT: true,
      });

      const handleExportExcel = () => {
        refetch && refetch('export', columns);
      };

      return (
        <Form form={form}>
          <TableComponent
            title={title}
            columns={columns}
            loading={loading}
            scroll={{ x: 'max-content' }}
            dataSource={data.items}
            rowKey={(record) => record.id}
            pagination={
              pagination && {
                current: data.pageInfo?.currentPage,
                pageSize: data.pageInfo?.itemsPerPage,
                total: data.pageInfo?.totalItems,
                onChange: HocChangePagination(),
              }
            }
          />
        </Form>
      );
    },
  ),
);
